import { Box, Card, CardContent, Tooltip, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import type { ReactNode, MouseEventHandler } from 'react'
import EmptyState from '../EmptyState/EmptyState'
import ErrorState from '../ErrorState/ErrorState'
import LoadingSkeleton from '../LoadingSkeleton/LoadingSkeleton'
import { colors } from 'constants/theme'
import { HeroContext, HeroColorKey } from './HeroContext'

export interface WidgetCardProps {
  title: ReactNode
  titleTooltip?: string
  subtitle?: ReactNode
  headerAction?: ReactNode
  notice?: ReactNode
  loading?: boolean
  error?: Error | null
  empty?: boolean
  emptyMessage?: ReactNode
  onRetry?: () => void
  skeletonHeight?: number
  onClick?: MouseEventHandler<HTMLDivElement>
  children?: ReactNode
  footer?: ReactNode
  sx?: Record<string, unknown>
  // 'plain' → dashboard Card chrome (white bg, lightGray border, soft shadow).
  // 'hero'  → SummaryBox chrome (colored gradient + white text); requires heroColor.
  variant?: 'plain' | 'hero'
  heroColor?: HeroColorKey
}

export default function WidgetCard({
  title,
  titleTooltip,
  subtitle,
  headerAction,
  notice,
  loading,
  error,
  empty,
  emptyMessage,
  onRetry,
  skeletonHeight = 280,
  onClick,
  children,
  footer,
  sx,
  variant = 'plain',
  heroColor,
}: WidgetCardProps) {
  const clickable = !!onClick
  const showLoading = !!loading
  const showError = !showLoading && !!error
  const showEmpty = !showLoading && !showError && !!empty
  const onHero = variant === 'hero' && !!heroColor

  // Style buckets — chosen by variant.
  const cardChrome = onHero
    ? {
        background: (colors as any).dashboard?.summaryBox?.[heroColor!],
        boxShadow: `0 0 6px 0 ${colors.black10}`,
        borderRadius: 2,
        color: colors.white,
        border: 'none',
      }
    : {
        // Premium "business report" chrome — slightly more visible border
        // (lightGray vs extraLightGray) to ground the card, but softer
        // shadow (black05 vs black16) so the whole grid feels airy. Wider
        // radius reads as "polished report" rather than "data table".
        backgroundColor: colors.white,
        border: `1px solid ${colors.lightGray}`,
        boxShadow: `0 1px 3px 0 ${colors.black05}`,
        borderRadius: 2,
        color: colors.textPrimary,
      }

  const titleColor = onHero ? colors.white : colors.textPrimary
  const subtitleColor = onHero ? 'rgba(255,255,255,0.85)' : colors.textLightGray
  // Plain header gets the dashboard-style bottom border under the title row;
  // hero header keeps the title flush with the body (no divider) so the
  // colored chrome looks uninterrupted.
  const headerBorder = onHero
    ? {}
    : { borderBottom: `1px solid ${colors.lightGray}`, pb: '12px' }

  return (
    <HeroContext.Provider value={{ onHero, heroColor }}>
      <Card
        onClick={onClick}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'box-shadow 160ms ease, transform 160ms ease',
          ...cardChrome,
          ...(clickable
            ? {
                cursor: 'pointer',
                '&:hover': { boxShadow: 4, transform: 'translateY(-1px)' },
              }
            : null),
          ...(sx || {}),
        }}
      >
        <CardContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            gap: 0.75,
            // Plain cards get a roomier 2.5 (20px) so content breathes —
            // hero kept tighter at 2 to preserve its compact KPI rhythm.
            // Tightens to 2 on xs so KPI tiles don't waste mobile width.
            p: onHero ? 2 : { xs: 2, sm: 2.5 },
            '&:last-child': { pb: onHero ? 2 : { xs: 2, sm: 2.5 } },
          }}
        >
          {/* Header row */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              minHeight: 28,
              ...headerBorder,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  // Weight 600 reads as section title rather than body —
                  // makes each widget feel like its own chapter.
                  fontWeight: 600,
                  fontSize: 16,
                  lineHeight: 1.3,
                  color: titleColor,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {title}
              </Typography>
              {titleTooltip && (
                <Tooltip
                  title={titleTooltip}
                  arrow
                  placement="top"
                  componentsProps={{ tooltip: { sx: { whiteSpace: 'pre-line', maxWidth: 400 } } }}
                >
                  <InfoOutlinedIcon
                    fontSize="small"
                    sx={{
                      color: onHero ? 'rgba(255,255,255,0.85)' : 'text.disabled',
                      cursor: 'help',
                      flexShrink: 0,
                    }}
                  />
                </Tooltip>
              )}
            </Box>
            {headerAction && (
              <Box
                onClick={(e) => e.stopPropagation()}
                sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}
              >
                {headerAction}
              </Box>
            )}
          </Box>

          {subtitle && (
            <Typography
              variant="caption"
              component="div"
              sx={{ display: 'block', color: subtitleColor }}
            >
              {subtitle}
            </Typography>
          )}

          {notice && !showLoading && !showError && !showEmpty && (
            <Box sx={{ mt: 0.5 }}>{notice}</Box>
          )}

          <Box sx={{ flex: 1, minHeight: 0, mt: 0.5 }}>
            {showLoading && <LoadingSkeleton height={skeletonHeight} />}
            {showError && <ErrorState onRetry={onRetry} />}
            {showEmpty && (
              <EmptyState message={typeof emptyMessage === 'string' ? emptyMessage : undefined} />
            )}
            {!showLoading && !showError && !showEmpty && children}
          </Box>

          {footer && !showLoading && !showError && !showEmpty && (
            <Box sx={{ mt: 1 }}>{footer}</Box>
          )}
        </CardContent>
      </Card>
    </HeroContext.Provider>
  )
}
